import { useState, useEffect } from 'react';
import { subscribeToDocument } from '../firebase/firestore';

/**
 * useHandover - Fetches and subscribes to handover status for a raffle
 *
 * @param raffleId - The raffle ID (also used as handover document ID)
 * @param userId - Optional user ID (skips fetch if not authenticated)
 * @param options - Optional config object
 * @param options.enabled - Whether to enable the fetch (default: true). Set to false to skip fetching.
 *
 * Returns:
 * - handover: The handover document data or null (null if no handover exists yet)
 * - loading: Boolean indicating if initial fetch is in progress
 * - winnerConfirmed: Boolean shorthand
 * - sellerConfirmed: Boolean shorthand
 * - bothConfirmed: Boolean - true when both parties have confirmed
 * - status: 'pending' | 'completed' | 'disputed' | null
 */
export function useHandover(raffleId, userId = null, options = {}) {
  const { enabled = true } = options;
  const [handover, setHandover] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Skip if not enabled, no raffleId, or no authenticated user
    if (!enabled || !raffleId || !userId) {
      setHandover(null);
      setLoading(false);
      return;
    }

    // Direct document read - handover doc ID is the raffleId
    const unsubscribe = subscribeToDocument(
      'handovers',
      raffleId,
      (data) => {
        setHandover(data);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching handover:', error);
        setHandover(null);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [raffleId, userId, enabled]);

  // Derived state for convenience
  const winnerConfirmed = handover?.winnerConfirmed || false;
  const sellerConfirmed = handover?.sellerConfirmed || false;
  const bothConfirmed = winnerConfirmed && sellerConfirmed;
  const status = handover?.status || null;

  // Winner intent state
  const winnerIntentConfirmed = handover?.winnerIntentConfirmed || false;
  const winnerIntentDeadline = handover?.winnerIntentDeadline?.toDate
    ? handover.winnerIntentDeadline.toDate()
    : handover?.winnerIntentDeadline
      ? new Date(handover.winnerIntentDeadline)
      : null;

  return {
    handover,
    loading,
    winnerConfirmed,
    sellerConfirmed,
    bothConfirmed,
    status,
    winnerIntentConfirmed,
    winnerIntentDeadline,
  };
}

/**
 * Calculate time remaining for winner to confirm collection
 * Winners have 48 hours from draw to confirm they'll collect
 */
export function getConfirmationDeadline(handover) {
  if (!handover?.createdAt) return null;

  const createdAt = handover.createdAt.toDate ? handover.createdAt.toDate() : new Date(handover.createdAt);
  const deadline = new Date(createdAt.getTime() + 48 * 60 * 60 * 1000); // 48 hours

  return deadline;
}

/**
 * Check if confirmation deadline has passed
 */
export function isConfirmationExpired(handover) {
  const deadline = getConfirmationDeadline(handover);
  if (!deadline) return false;

  return new Date() > deadline;
}

export default useHandover;
